import { t } from 'i18next';

export type WebchatMessage = {
  id: number;
  conversation_id: number;
  sender_type: 'visitor' | 'agent' | 'bot' | 'system';
  original_text: string;
  translated_text: string | null;
  original_language: string;
  translated_language: string | null;
  audio_url: string | null;
  sender_id: number | null;
  sender_name: string | null;
  created_at: string;
};

// A single transcript turn streamed from the ElevenLabs monitor WebSocket via
// the CRM's /api/v2/webhooks/elevenlabs/live endpoint. Used by AI calls only;
// webchat sessions still use WebchatMessage.
//
// `text` is always the original-language utterance straight from Scribe.
// `translated_text` is the English translation produced by gpt-5.4-nano in
// the same call that detected `original_language`. Both translation fields
// may be null when the LLM proxy errored — the bubble falls back to `text`
// in that case.
export type LiveTranscriptTurn = {
  role: 'user' | 'agent' | 'system';
  text?: string;
  type: 'user_transcript' | 'agent_response' | 'agent_response_correction' | 'translation' | 'bridge_end';
  translated_text?: string | null;
  original_language?: string | null;
  translated_language?: string | null;
  original_text?: string | null;
  time_in_call_secs?: number | null;
  received_at: string;
  turn_id?: string;
  original_audio_s3_key?: string | null;
  translated_audio_s3_key?: string | null;
  reason?: string;
};

// All STI subclasses surfaced by the Command Center. Keep aligned with the
// Rails-side `Conversation::COMMAND_CENTER_TYPES` constant.
export const CONVERSATION_TYPES = [
  'ChatConversation',
  'ElevenLabsCall',
  'AnalyticsCall',
  'AgentCall',
  'TextConversation',
  'EmailConversation',
] as const;
export type ConversationType = (typeof CONVERSATION_TYPES)[number];

export type WebchatConversation = {
  id: number;
  type: ConversationType | string;
  sid: string;
  language: string;
  webchat_status: WebchatStatus;
  assigned_agent_id: number | null;
  assigned_agent_name: string | null;
  company_id: number;
  company_name: string | null;
  user_id: number | null;
  visitor_name: string | null;
  visitor_phone: string | null;
  phone: string | null;
  disconnected_at: string | null;
  created_at: string;
  updated_at: string;
  unread_count: number;
  last_message: WebchatMessage | null;
  idle_since: string;
  notes: string | null;
  convo_summary: string | null;
  duration: number | null;
  live_transcript_turns?: LiveTranscriptTurn[];
  eleven_labs_conversation_id?: string | null;
  // True when the linked User has prior conversations. Powers the small
  // "Returning" tag on session cards.
  returning_caller?: boolean;
};

export const WEBCHAT_STATUSES = ['bot', 'queued', 'live', 'closed', 'claiming'] as const;
export type WebchatStatus = (typeof WEBCHAT_STATUSES)[number];

// Non-fatal anomalies the LLM bridge surfaces during a translation call.
// Currently only one kind is emitted, but the union is open-ended so future
// fallbacks (translation provider switch, STT degraded mode, etc.) can plug
// in without churning the store shape.
export type BridgeWarningKind = 'voice_rejected';

export type BridgeWarning = {
  kind: BridgeWarningKind;
  role: 'visitor' | 'agent';
  details: Record<string, string | number | null | undefined>;
};

export const WEBCHAT_STATUS_COLORS: Record<WebchatStatus, string> = {
  bot: 'blue',
  queued: 'orange',
  live: 'green',
  closed: 'default',
  claiming: 'orange',
};

export function getVisitorLabel(conversation: WebchatConversation): string {
  return conversation.visitor_name || conversation.visitor_phone || conversation.phone || t('webchat:visitor.anonymous');
}

// True for AI-handled voice calls — currently ElevenLabsCall (modern
// ConvAI path) and AnalyticsCall (legacy rai path). Both populate
// live_transcript_turns the same way, both can be claimed through
// LiveCalls::ClaimService, and both surface as listen-only/transcript
// UX on the dashboard, so the call-site checks behave identically.
// STI discriminator from the Rails Conversation model.
export function isAiCallSession(conversation: { type?: string } | null | undefined): boolean {
  return conversation?.type === 'ElevenLabsCall' || conversation?.type === 'AnalyticsCall';
}

export type QueueStats = {
  queued: number;
  live: number;
  bot: number;
  // Multi-channel headlines added alongside the webchat counts above.
  // `voice` is in-flight RAI voice calls (AnalyticsCall.end_reason IS NULL);
  // `sms` is open SMS threads (TextConversation, end_reason NULL or 'agent').
  voice?: number;
  sms?: number;
  longest_wait: number;
  available_agent_count: number;
  agents: AvailableAgent[];
};

export type AvailableAgent = {
  id: number;
  name: string;
  active_count: number;
  webchat_available: boolean;
};

export type WebchatHistoryResponse = {
  conversations: WebchatConversation[];
  total: number;
  page: number;
  per_page: number;
};

export type WebchatHistoryParams = {
  page?: number;
  per_page?: number;
  status?: WebchatStatus;
  // STI type filter ('ChatConversation' | 'ElevenLabsCall' | ...). Reps use
  // it to narrow the History table to a single channel.
  channel?: ConversationType;
  query?: string;
};

// WebSocket events from WebchatAgentChannel
export type WebchatAgentEvent =
  | { type: 'active_sessions'; sessions: WebchatConversation[] }
  | {
      type:
        | 'session_created'
        | 'session_started'
        | 'session_queued'
        | 'session_assigned'
        | 'session_closed'
        | 'session_transferred';
      conversation_id: number;
      conversation: WebchatConversation;
      from_agent_id?: number;
      to_agent_id?: number;
      // Bridge metadata (present when session_assigned is from an AI-call claim)
      mode?: 'translated_bridge';
      agent_language?: string;
      visitor_language?: string;
      // Error message from PerformGracefulTransferJob rollback (claim failed)
      error?: string;
    }
  | { type: 'language_detected'; conversation_id: number; language: string }
  | { type: 'bridge_status'; conversation_id: number; role: 'visitor' | 'agent'; status: 'transcribing' | 'translating' | 'speaking' | 'idle' }
  | {
      type: 'bridge_warning';
      conversation_id: number;
      role: 'visitor' | 'agent';
      kind: BridgeWarningKind;
      details: Record<string, string | number | null | undefined>;
    }
  | {
      type: 'new_message';
      conversation_id: number;
      message: WebchatMessage;
    }
  | {
      type: 'live_transcript';
      conversation_id: number;
      eleven_labs_conversation_id: string;
      turn: LiveTranscriptTurn;
    }
  | { type: 'visitor_typing'; conversation_id: number }
  | { type: 'visitor_disconnected'; conversation_id: number; conversation: WebchatConversation }
  | { type: 'visitor_reconnected'; conversation_id: number; conversation: WebchatConversation }
  | { type: 'idle_warning'; conversation_id: number; idle_since: string }
  | { type: 'agent_availability_changed'; agent_id: number; agent_name: string; webchat_available: boolean }
  | { type: 'translate_toggled'; conversation_id: number; translate: boolean }
  // Phase 1 visitor screen share. Visitor's MediaRecorder ships webm
  // chunks over the chat WS; CRM relays them to this agent's
  // personal topic. The dashboard reassembles via MediaSource —
  // forward-only, no persistence.
  | { type: 'screen_started'; conversation_id: number; mime: string }
  | { type: 'screen_chunk'; conversation_id: number; chunk: string; seq: number; first: boolean }
  | { type: 'screen_ended'; conversation_id: number };

export type VoiceTokenResponse = {
  token: string;
  identity: string;
  ttl: number;
};

export type StartConferencePayload = {
  from_number_id: number;
  participant_numbers?: string[];
  translation_enabled?: boolean;
  visitor_number?: string;
};

export type StartConferenceResponse = {
  conference_name?: string;
  participant_call_sids?: string[];
  mode?: 'translated';
  visitor_call_sid?: string;
  conversation_id?: number;
};

export type EndConferencePayload = {
  call_sids: string[];
};

export type EndConferenceResponse = {
  ok: boolean;
};

export type ConferenceState = 'idle' | 'initiating' | 'active' | 'ending' | 'ended' | 'error';
